import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, doc, onSnapshot, serverTimestamp, updateDoc } from 'firebase/firestore';
import { MapPin, Pencil, Phone, Plus } from 'lucide-react';
import { db } from '../../lib/firebase';
import { useAuth } from '../../lib/auth';
import { DEMO_BOOKINGS, lesseeByUid } from '../../lib/demoData';
import type { AircraftDoc, BookingDoc, BookingStatus, FlightApprovalDoc } from '../../lib/types';
import { isStudentPilot } from '../../lib/eligibility';
import { approvalState } from '../../lib/flightApproval';
import { PageHeader } from '../../components/PageHeader';
import { MonthCalendar, type MaintBlock } from '../../components/MonthCalendar';
import { BookingEditModal } from '../../components/BookingEditModal';
import { TRACKED_REG } from '../../lib/flightTrack';

const STATUS_BADGE: Record<BookingStatus, string> = {
  auto_confirmed: 'badge-success',
  pending_approval: 'badge-success',
  approved: 'badge-success',
  denied: 'badge-danger',
  cancelled: 'badge-neutral',
  completed: 'badge-info',
};
const STATUS_LABEL: Record<BookingStatus, string> = {
  auto_confirmed: 'Confirmed',
  pending_approval: 'Confirmed',
  approved: 'Confirmed',
  denied: 'Denied',
  cancelled: 'Cancelled',
  completed: 'Completed',
};

const fmt = (d: Date) =>
  d.toLocaleString([], { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

type BookingFilter = 'upcoming' | 'past' | 'cancelled' | 'all';
const FILTERS: { key: BookingFilter; label: string }[] = [
  { key: 'upcoming', label: 'Upcoming' },
  { key: 'past', label: 'Past' },
  { key: 'cancelled', label: 'Cancelled' },
  { key: 'all', label: 'All' },
];

export function AdminBookings() {
  const { demoMode, profile } = useAuth();
  const [bookings, setBookings] = useState<BookingDoc[] | null>(null);
  const [ac, setAc] = useState<AircraftDoc | null>(null);
  const [view, setView] = useState<'calendar' | 'list'>('list');
  const [filter, setFilter] = useState<BookingFilter>('upcoming');
  // The booking pending cancellation — drives the reason modal. The owner must
  // affirm WHY (operational-control guard); we never just flip status silently.
  // That guard exists because cancelling ANOTHER pilot's reservation is the
  // operational-control problem. Cancelling your own isn't, so `isMine` drops
  // the reason gate and the "will be notified" line for the owner's own slots.
  const [cancelTarget, setCancelTarget] = useState<BookingDoc | null>(null);
  // The booking being rescheduled. Only ever one of the owner's own slots — moving
  // another pilot's reservation would be the same operational-control problem the
  // cancel-reason gate exists to prevent, so they get the cancel flow instead.
  const [editTarget, setEditTarget] = useState<BookingDoc | null>(null);

  useEffect(() => {
    if (demoMode) {
      setBookings(DEMO_BOOKINGS);
      return;
    }
    if (!db) return;
    const unsub = onSnapshot(collection(db, 'bookings'), (snap) =>
      setBookings(snap.docs.map((d) => ({ ...(d.data() as BookingDoc), bookingId: d.id })))
    );
    return () => unsub();
  }, [demoMode]);
  // Student bookings carry a named CFI's per-flight approval (Old Republic SPR).
  const [approvals, setApprovals] = useState<Record<string, FlightApprovalDoc>>({});
  useEffect(() => {
    if (demoMode || !db) return;
    return onSnapshot(collection(db, 'flightApprovals'), (snap) => {
      const m: Record<string, FlightApprovalDoc> = {};
      snap.docs.forEach((d) => (m[d.id] = d.data() as FlightApprovalDoc));
      setApprovals(m);
    });
  }, [demoMode]);
  useEffect(() => {
    if (demoMode || !db) return;
    return onSnapshot(collection(db, 'aircraft'), (snap) => {
      const d = snap.docs[0];
      if (d) setAc({ ...(d.data() as AircraftDoc), aircraftId: d.id });
    });
  }, [demoMode]);

  // The shop window (annual etc.) — same red block the Book-a-Flight calendar shows.
  const maintBlocks: MaintBlock[] =
    ac?.maintWindowStart && ac?.maintWindowEnd
      ? [{
          start: new Date(`${ac.maintWindowStart}T00:00:00`),
          end: new Date(`${ac.maintWindowEnd}T00:00:00`),
          label: ac.maintWindowLabel || 'Scheduled maintenance',
          tone: 'red',
        }]
      : [];


  async function doCancel(b: BookingDoc, cancelReason: 'lessee_request' | 'asset_protection') {
    if (demoMode) {
      setBookings((curr) =>
        (curr ?? []).map((x) => (x.bookingId === b.bookingId ? { ...x, status: 'cancelled', cancelReason } : x))
      );
      setCancelTarget(null);
      return;
    }
    if (!db) return;
    await updateDoc(doc(db, 'bookings', b.bookingId), {
      status: 'cancelled',
      cancelReason,
      cancelledAt: serverTimestamp(),
    });
    setCancelTarget(null);
  }

  const now = Date.now();
  const isMine = (b: BookingDoc) => Boolean(profile?.uid) && b.lesseeUid === profile?.uid;
  const isCancelled = (b: BookingDoc) => b.status === 'cancelled' || b.status === 'denied';
  const isPast = (b: BookingDoc) => b.endDateTime.toDate().getTime() < now;
  const inFilter = (b: BookingDoc, f: BookingFilter) => {
    if (f === 'all') return true;
    if (f === 'cancelled') return isCancelled(b);
    if (f === 'upcoming') return !isCancelled(b) && !isPast(b);
    return !isCancelled(b) && isPast(b); // 'past'
  };
  const all = bookings ?? [];
  const counts = {
    upcoming: all.filter((b) => inFilter(b, 'upcoming')).length,
    past: all.filter((b) => inFilter(b, 'past')).length,
    cancelled: all.filter((b) => inFilter(b, 'cancelled')).length,
    all: all.length,
  };
  const sorted = all
    .filter((b) => inFilter(b, filter))
    .sort((a, b) => {
      const ta = a.startDateTime.toDate().getTime();
      const tb = b.startDateTime.toDate().getTime();
      // Upcoming: soonest first. Everything else: most recent first.
      return filter === 'upcoming' ? ta - tb : tb - ta;
    });

  return (
    <>
      <PageHeader
        title="Schedule"
        subtitle={`The full schedule for ${TRACKED_REG}. Any active pilot books freely — no approvals.`}
        actions={
          <div className="flex flex-wrap gap-2">
            <div className="flex gap-2">
              <button
                onClick={() => setView('list')}
                className={view === 'list' ? 'btn-primary py-1.5 text-sm' : 'btn-secondary py-1.5 text-sm'}
              >
                List
              </button>
              <button
                onClick={() => setView('calendar')}
                className={view === 'calendar' ? 'btn-primary py-1.5 text-sm' : 'btn-secondary py-1.5 text-sm'}
              >
                Calendar
              </button>
            </div>
            <Link to="/admin/book" className="btn-primary py-1.5 text-sm flex items-center gap-1.5">
              <Plus size={15} /> Book the plane
            </Link>
          </div>
        }
      />

      {view === 'calendar' ? (
        <MonthCalendar bookings={bookings ?? []} maintBlocks={maintBlocks} onPickBooking={(b) => setEditTarget(b)} />
      ) : !bookings ? null : (
        <>
          <div className="mb-4 flex flex-wrap gap-2">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={`rounded-full px-3 py-1.5 text-xs font-semibold transition ${
                  filter === f.key
                    ? 'bg-brand text-white'
                    : 'bg-slate-100 text-ink-muted hover:bg-slate-200'
                }`}
              >
                {f.label}
                <span className={filter === f.key ? 'ml-1.5 text-sky-100' : 'ml-1.5 text-ink-subtle'}>
                  {counts[f.key]}
                </span>
              </button>
            ))}
          </div>

          {sorted.length === 0 ? (
            <div className="card p-8 text-center text-ink-muted">
              {filter === 'upcoming'
                ? 'No upcoming bookings.'
                : filter === 'cancelled'
                ? 'No cancelled bookings.'
                : filter === 'past'
                ? 'No past bookings.'
                : 'No bookings yet.'}
            </div>
          ) : (
            <div className="space-y-3">
              {sorted.map((b) => {
            const u = lesseeByUid(b.lesseeUid);
            const name =
              b.lesseeName ||
              `${u?.personal?.firstName ?? ''} ${u?.personal?.lastName ?? ''}`.trim() ||
              'Pilot';
            const phone = b.lesseePhone || u?.personal?.phone;
            const active = b.status === 'auto_confirmed' || b.status === 'approved';
            const past = b.endDateTime.toDate().getTime() < Date.now();
            return (
              <div key={b.bookingId} className={`card p-4 ${past ? 'opacity-70' : ''}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-medium text-navy-900">{name}</div>
                    <div className="text-xs text-ink-muted">
                      {fmt(b.startDateTime.toDate())} – {fmt(b.endDateTime.toDate())}
                    </div>
                    {b.flightPurpose === 'discovery' && (
                      <div className="text-xs font-medium text-sky-700">
                        Discovery flight{b.discoveryGuest ? ` with ${b.discoveryGuest}` : ''} · guest pays $170/hr, fuel incl. ·{' '}
                        {b.waiverId ? 'waiver signed ✓' : 'waiver not signed'}
                        {b.waiverId && b.waiverUsCitizen === false && ' · not a U.S. citizen: TSA approval needed before any training'}
                        {!b.waiverId && !past && b.lesseeUid === profile?.uid && (
                          <>
                            {' · '}
                            <Link to={`/admin/discovery/waiver?booking=${b.bookingId}`} className="underline">Guest waiver</Link>
                          </>
                        )}
                      </div>
                    )}
                    {isStudentPilot(u?.pilot) && !past && (() => {
                      const ap = approvals[b.bookingId];
                      const st = approvalState(b, ap);
                      return (
                        <div className={`text-xs font-medium ${st === 'approved' ? 'text-emerald-700' : 'text-amber-700'}`}>
                          Student ·{' '}
                          {st === 'approved'
                            ? `CFI approved (${ap?.kind}) by ${ap?.cfiName}`
                            : st === 'stale'
                            ? 'moved since CFI approval, needs re-approval'
                            : 'no CFI approval yet'}
                        </div>
                      );
                    })()}
                    {b.expectedDestinations && (
                      <div className="flex items-center gap-1.5 text-xs text-ink-muted">
                        <MapPin size={12} className="shrink-0 text-ink-subtle" />
                        {b.expectedDestinations}
                      </div>
                    )}
                  </div>
                  <span className={`${STATUS_BADGE[b.status]} shrink-0 whitespace-nowrap`}>
                    {STATUS_LABEL[b.status]}
                  </span>
                </div>

                <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-slate-100 pt-3">
                  {phone && (
                    <a
                      href={`tel:${phone.replace(/[^0-9+]/g, '')}`}
                      className="btn-secondary inline-flex min-h-0 items-center gap-1.5 px-3 py-1.5 text-xs"
                    >
                      <Phone size={13} /> {phone}
                    </a>
                  )}
                  {active && !past && b.lesseeUid === profile?.uid && (
                    <button
                      onClick={() => setEditTarget(b)}
                      className="btn-secondary inline-flex min-h-0 items-center gap-1.5 px-3 py-1.5 text-xs"
                    >
                      <Pencil size={13} /> Change time
                    </button>
                  )}
                  {active && !past && (
                    <button
                      onClick={() => setCancelTarget(b)}
                      className="btn-danger ml-auto min-h-0 px-3 py-1.5 text-xs"
                    >
                      Cancel booking
                    </button>
                  )}
                </div>
              </div>
            );
              })}
            </div>
          )}
        </>
      )}

      {editTarget && (
        <BookingEditModal
          booking={editTarget}
          onClose={() => setEditTarget(null)}
          // Live snapshot repaints itself; demo mode has no listener, so re-render
          // after the sample object is mutated in place.
          onSaved={() => setBookings((cur) => (cur ? [...cur] : cur))}
          onCancel={() => setCancelTarget(editTarget)}
        />
      )}

      {cancelTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-navy-900/40 p-4 backdrop-blur-sm">
          <div className="card w-full max-w-md p-6">
            {isMine(cancelTarget) ? (
              <>
                <h3 className="font-display text-lg font-semibold text-navy-900">Cancel your booking?</h3>
                <p className="mt-2 text-sm text-ink-muted">
                  {fmt(cancelTarget.startDateTime.toDate())} — the slot goes back on the calendar for
                  everyone else. Nobody is notified.
                </p>
                <button
                  onClick={() => doCancel(cancelTarget, 'lessee_request')}
                  className="btn-danger mt-5 w-full"
                >
                  Cancel booking
                </button>
                <button onClick={() => setCancelTarget(null)} className="btn-ghost mt-2 w-full text-sm">
                  Keep it
                </button>
              </>
            ) : (
              <>
                <h3 className="font-display text-lg font-semibold text-navy-900">Cancel this booking?</h3>
                <p className="mt-2 text-sm text-ink-muted">
                  {cancelTarget.lesseeName ?? 'The pilot'} will be
                  notified. As the owner, confirm <strong>why</strong> you’re cancelling — you can’t cancel just to
                  take the slot (the lessee controls their reservation).
                </p>
                <div className="mt-4 space-y-2">
                  <button
                    onClick={() => doCancel(cancelTarget, 'lessee_request')}
                    className="btn-secondary w-full justify-start text-left"
                  >
                    <span>
                      <span className="block font-semibold text-navy-900">At the lessee’s request</span>
                      <span className="block text-xs text-ink-muted">They asked me to cancel it.</span>
                    </span>
                  </button>
                  <button
                    onClick={() => doCancel(cancelTarget, 'asset_protection')}
                    className="btn-secondary w-full justify-start text-left"
                  >
                    <span>
                      <span className="block font-semibold text-navy-900">Asset protection</span>
                      <span className="block text-xs text-ink-muted">
                        Maintenance, airworthiness, or insurance — the aircraft can’t safely/legally fly.
                      </span>
                    </span>
                  </button>
                </div>
                <button onClick={() => setCancelTarget(null)} className="btn-ghost mt-4 w-full text-sm">
                  Keep the booking
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
